import type { JSX } from "react";
import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams, useOutletContext } from "react-router-dom";
import { EmptyState } from "@mailflare/ui";
import type { MailboxWithCounts, UserPublic } from "@mailflare/shared";

import { FullPageSpinner } from "@/components/full-page-spinner";
import { LiveAnnouncer } from "@/components/live-announcer";
import { ShortcutsHelpDialog } from "@/components/shortcuts-help-dialog";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import { useSession } from "@/features/auth/hooks";
import { ComposeModal } from "@/features/compose/compose-modal";
import { useLabels } from "@/features/labels/hooks";
import { selectMailbox, useMailboxes } from "@/features/mailboxes/hooks";
import { routes } from "@/features/mailboxes/routes";
import { useUiStore } from "@/stores/ui-store";
import { useComposeStore } from "@/stores/compose-store";

import { MailboxSwitcher } from "./mailbox-switcher";
import { MobileSidebar } from "./mobile-sidebar";
import { Sidebar } from "@/features/mailboxes/components/sidebar";
import { ThemeToggle } from "@/components/theme-toggle";
import { Topbar } from "./topbar";
import { UserMenu } from "./user-menu";
import { SearchBar } from "@/features/search/search-bar";

export interface AppShellContextValue {
  readonly user: UserPublic;
  readonly mailbox: MailboxWithCounts | null;
  readonly mailboxes: readonly MailboxWithCounts[];
}

export function useAppShell(): AppShellContextValue {
  return useOutletContext<AppShellContextValue>();
}

function focusSearch(): void {
  const input = document.getElementById("global-search");
  if (input instanceof HTMLInputElement) input.focus();
}

export function AppShell(): JSX.Element {
  const session = useSession();
  const mailboxesQuery = useMailboxes();
  const labelsQuery = useLabels();
  const params = useParams();
  const location = useLocation();

  const sidebarOpen = useUiStore((state) => state.sidebarOpen);
  const setSidebarOpen = useUiStore((state) => state.setSidebarOpen);
  const shortcutsOpen = useUiStore((state) => state.shortcutsOpen);
  const setShortcutsOpen = useUiStore((state) => state.setShortcutsOpen);
  const openCompose = useComposeStore((state) => state.openCompose);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, setSidebarOpen]);

  useKeyboardShortcuts({
    onCompose: () => openCompose(),
    onFocusSearch: focusSearch,
    onToggleHelp: () => setShortcutsOpen(!shortcutsOpen),
    onEscape: () => {
      setSidebarOpen(false);
      setShortcutsOpen(false);
    },
  });

  if (session.isLoading || mailboxesQuery.isLoading) return <FullPageSpinner />;
  if (session.data === undefined) {
    return <Navigate to={routes.login} replace state={{ from: location.pathname }} />;
  }

  const user = session.data.user;
  const mailboxes = mailboxesQuery.data ?? [];
  const mailbox = selectMailbox(mailboxes, params["mailboxId"]) ?? null;
  const labels = labelsQuery.data ?? [];

  return (
    <div className="bg-muted/40 flex h-dvh overflow-hidden">
      <a
        href="#main-content"
        className="bg-background border-border sr-only rounded-md px-3 py-2 text-sm shadow-lg focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50"
      >
        Skip to main content
      </a>

      {mailbox !== null ? (
        <aside className="hidden w-64 shrink-0 md:block">
          <Sidebar mailboxId={mailbox.id} labels={labels} unreadCount={mailbox.unreadCount} />
        </aside>
      ) : null}

      {mailbox !== null ? (
        <MobileSidebar
          open={sidebarOpen}
          onOpenChange={setSidebarOpen}
          mailboxId={mailbox.id}
          labels={labels}
          unreadCount={mailbox.unreadCount}
        />
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          onOpenMenu={() => setSidebarOpen(true)}
          onOpenShortcuts={() => setShortcutsOpen(true)}
          onCompose={() => openCompose()}
          canCompose={mailbox !== null}
          trailing={
            <div className="flex items-center gap-1">
              <MailboxSwitcher mailboxes={mailboxes} current={mailbox} />
              <ThemeToggle />
              <UserMenu user={user} />
            </div>
          }
        >
          {mailbox !== null ? <SearchBar mailboxId={mailbox.id} /> : <div className="flex-1" />}
        </Topbar>

        <main id="main-content" className="flex min-h-0 flex-1 flex-col md:pr-3 md:pb-3">
          <div className="bg-card border-border flex min-h-0 flex-1 flex-col overflow-hidden md:rounded-xl md:border">
            {mailbox === null && mailboxes.length === 0 ? (
              <EmptyState
                title="No mailbox yet"
                description="Your account has no mailbox. Create one from Settings to start sending and receiving mail."
                className="m-auto"
              />
            ) : (
              <Outlet context={{ user, mailbox, mailboxes } satisfies AppShellContextValue} />
            )}
          </div>
        </main>
      </div>

      <LiveAnnouncer />
      <ShortcutsHelpDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <ComposeModal mailbox={mailbox} user={user} />
    </div>
  );
}
